import { useMemo, useState } from "react";
import { Link } from "react-router";
import { ArrowLeft, Calendar, FileText, Sparkles, Users } from "lucide-react";
import type { PermissionKey } from "@bookdental/schemas";
import { useSetPageHeader } from "@/lib/page-header";
import { useAuth } from "@/lib/auth";
import { useIsMobile } from "@/lib/use-media-query";
import { ImportPatientsSection } from "@/components/settings/import/ImportPatientsSection";
import { ImportAppointmentsSection } from "@/components/settings/import/ImportAppointmentsSection";
import { ImportClinicalRecordsSection } from "@/components/settings/import/ImportClinicalRecordsSection";
import { DemoDataSection } from "@/components/settings/DemoDataSection";
import { useTourDaPagina } from "@/components/product-tour/PageTourEntry";
import { CONFIG_IMPORTAR_TOUR_STEPS } from "@/lib/product-tour/page-tour-steps";

type SectionKey = "patients" | "appointments" | "records" | "demo";

/** `requiresPermission` espelha o gate do servidor — sem isso a seção aparece pra quem não pode
 *  usá-la e o clique vira erro (`patients.manage` e `agenda.manage` são exigidos em
 *  `import.patients`, `import.appointments`, `import.deleteBatch`, `demo.seed` e `demo.clear`). */
const SECTIONS: {
  key: SectionKey;
  label: string;
  description: string;
  icon: React.ReactNode;
  accent: string;
  requiresPermission?: PermissionKey;
}[] = [
  {
    key: "patients",
    label: "Pacientes",
    description: "Cadastro de pacientes em CSV ou Excel",
    icon: <Users className="h-4 w-4" />,
    accent: "#2457ff",
    requiresPermission: "patients.manage",
  },
  {
    key: "appointments",
    label: "Agenda",
    description: "Consultas agendadas históricas",
    icon: <Calendar className="h-4 w-4" />,
    accent: "#0ea5e9",
    requiresPermission: "agenda.manage",
  },
  {
    key: "records",
    label: "Fichas clínicas",
    description: "Anamneses e prontuários",
    icon: <FileText className="h-4 w-4" />,
    accent: "#10b981",
  },
  {
    key: "demo",
    label: "Dados de exemplo",
    description: "Popular ou limpar dados fictícios",
    icon: <Sparkles className="h-4 w-4" />,
    accent: "#f59e0b",
    requiresPermission: "patients.manage",
  },
];

/** `embutido`: dentro de uma seção de outra página (Configurações, Minha conta) — sem título
 *  próprio, sem "voltar" e com as subseções em abas, já que a de fora tem a barra lateral. */
export function SettingsImport({ embutido = false }: { embutido?: boolean } = {}) {
  const compacto = useIsMobile() || embutido;
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const permissoes = user?.activeOrganization?.permissions;
  const secoes = useMemo(
    () => SECTIONS.filter((s) => !s.requiresPermission || permissoes?.[s.requiresPermission]),
    [permissoes],
  );

  const [section, setSection] = useState<SectionKey>("patients");
  // Muda toda vez que o tour reseta a seção de importação — força remount de ImportPatientsSection
  // (que guarda o passo do wizard só em estado local) de volta pro passo 0, senão o passo 2 do
  // tour ("config-importar-arquivo") pode não existir se o usuário já tiver avançado o wizard.
  const [importResetKey, setImportResetKey] = useState(0);

  // Embutida, quem registra o tour é a página de fora (registrar aqui seria sobrescrito).
  useTourDaPagina(embutido ? [] : CONFIG_IMPORTAR_TOUR_STEPS, () => {
    setSection("patients");
    setImportResetKey((k) => k + 1);
  });
  useSetPageHeader(
    {
      title: isMobile ? "Importar dados" : "Configurações · Importar banco de dados",
      subtitle: isMobile ? undefined : "Migre dados de outra plataforma",
    },
    [isMobile],
    !embutido,
  );
  // Se o papel não alcança a seção selecionada (inclusive a default), cai na primeira permitida.
  const activeSection = secoes.find((s) => s.key === section) ?? secoes[0];

  if (!activeSection) {
    return (
      <div className={`${embutido ? "" : "mx-auto max-w-6xl "}w-full space-y-4 pb-10`}>
        {!embutido && (
          <Link
            to="/configuracoes"
            className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900"
          >
            <ArrowLeft className="h-4 w-4" />
            Configurações
          </Link>
        )}
        <div className="rounded-xl border border-zinc-200 bg-white p-8 text-center shadow-sm">
          <p className="text-sm text-zinc-500">
            Seu papel não tem permissão pra importar dados nesta organização.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`${embutido ? "" : "mx-auto max-w-6xl "}w-full space-y-4 pb-10`}>
      {!embutido && (
        <Link
          to="/configuracoes"
          className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900"
        >
          <ArrowLeft className="h-4 w-4" />
          Configurações
        </Link>
      )}

      {compacto ? (
        <MobileTabs secoes={secoes} section={activeSection.key} onChange={setSection} />
      ) : null}

      <div className="flex gap-6">
        {!compacto && <DesktopSidebar secoes={secoes} section={activeSection.key} onChange={setSection} />}

        <div className="min-w-0 flex-1">
          <div className="rounded-xl border border-zinc-200 bg-white shadow-sm">
            <div className="flex items-center gap-3 border-b border-zinc-100 px-6 py-4">
              <div
                className="flex h-9 w-9 items-center justify-center rounded-lg"
                style={{
                  backgroundColor: `${activeSection.accent}1a`,
                  color: activeSection.accent,
                }}
              >
                {activeSection.icon}
              </div>
              <div>
                <h2 className="text-sm font-semibold text-zinc-900">
                  {activeSection.label}
                </h2>
                <p className="text-xs text-zinc-500">{activeSection.description}</p>
              </div>
            </div>

            <div className="px-4 py-5 sm:px-6">
              {activeSection.key === "patients" && <ImportPatientsSection key={importResetKey} />}
              {activeSection.key === "appointments" && <ImportAppointmentsSection />}
              {activeSection.key === "records" && <ImportClinicalRecordsSection />}
              {activeSection.key === "demo" && <DemoDataSection />}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function DesktopSidebar({
  secoes,
  section,
  onChange,
}: {
  secoes: typeof SECTIONS;
  section: SectionKey;
  onChange: (s: SectionKey) => void;
}) {
  return (
    <aside data-tour="config-importar-secoes" className="w-60 shrink-0">
      <nav className="sticky top-20 space-y-1">
        {secoes.map((s) => {
          const active = section === s.key;
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => onChange(s.key)}
              className={`group flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors ${
                active
                  ? "bg-zinc-50 text-zinc-900"
                  : "text-zinc-700 hover:bg-zinc-50"
              }`}
            >
              <span
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md transition-transform group-hover:scale-105"
                style={{
                  backgroundColor: `${s.accent}1a`,
                  color: s.accent,
                }}
              >
                {s.icon}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold">{s.label}</p>
                <p className="truncate text-[11px] text-zinc-500 group-hover:text-zinc-600">
                  {s.description}
                </p>
              </div>
            </button>
          );
        })}
      </nav>
    </aside>
  );
}

function MobileTabs({
  secoes,
  section,
  onChange,
}: {
  secoes: typeof SECTIONS;
  section: SectionKey;
  onChange: (s: SectionKey) => void;
}) {
  return (
    <div data-tour="config-importar-secoes" className="-mx-4 overflow-x-auto border-b border-zinc-200 bg-white sm:-mx-6">
      <div className="flex gap-1 px-4 sm:px-6">
        {secoes.map((s) => {
          const active = section === s.key;
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => onChange(s.key)}
              className={`inline-flex shrink-0 items-center gap-1.5 border-b-2 px-3 py-2.5 text-xs font-medium transition-colors ${
                active
                  ? "border-brand-600 text-brand-700"
                  : "border-transparent text-zinc-600 hover:text-zinc-900"
              }`}
            >
              {s.icon}
              {s.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
